import { useState, useRef, useEffect } from 'react'
import { useLocalWallet } from './WalletContext'
import { getSOLBalance } from '../utils/jupiter'
import { isBiometricAvailable } from '../utils/webauthnPrf'
import { getRememberPref, setRememberPref } from '../utils/unlockCache'
import { FingerprintIcon, PadlockIcon, PlusIcon } from './WalletIcons'
import { paperAccount, PAPER_EVENT } from '../utils/paperTrade'   // paperwallet-20261027

export default function WalletSetup() {
  const {
    publicKey, isLocked, hasStoredWallet,
    importKey, unlock, lock, removeWallet,
    hasBiometric, enableBiometric, unlockWithBiometric, disableBiometric,
    otherWallets, activePub, addWallet, switchWallet,
    paperActive, paperAdded, enterPaper, exitPaper,
  } = useLocalWallet()

  const [mode, setMode] = useState(null)       // null | 'import' | 'unlock' | 'add' | 'plus'
  // paperwallet-20261027: paper SOL balance + net (realized) PnL, live.
  const [paperAcct, setPaperAcct] = useState(paperAccount)
  useEffect(() => {
    const h = () => setPaperAcct(paperAccount())
    window.addEventListener(PAPER_EVENT, h)
    return () => window.removeEventListener(PAPER_EVENT, h)
  }, [])
  const [rememberOn, setRememberOn] = useState(getRememberPref)
  const [keyInput, setKeyInput] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Does this device have a platform authenticator at all? Async, so it can't
  // gate the first paint — the enroll row simply appears once known. PRF
  // support proper is only provable by enrolling, so a device can pass this
  // check and still fail at enrollment; handleEnableBio surfaces that.
  const [bioAvailable, setBioAvailable] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  // SOL balance beside the address. Same source WalletPopover uses, so both
  // views agree. Fetched when the wallet is usable and refreshed while the
  // dropdown is open — no background polling when nothing is looking at it.
  const [solBalance, setSolBalance] = useState(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
        setMode(null)
        setError('')
        setPassword('')
        setConfirmPw('')
        setKeyInput('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Auto-focus password input
  useEffect(() => {
    if (mode && inputRef.current) inputRef.current.focus()
  }, [mode])

  useEffect(() => {
    let alive = true
    isBiometricAvailable().then(ok => { if (alive) setBioAvailable(ok) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!publicKey || isLocked) { setSolBalance(null); return }
    let alive = true
    const pull = () => {
      getSOLBalance(publicKey)
        .then(b => { if (alive) setSolBalance(b) })
        .catch(() => { if (alive) setSolBalance(null) })
    }
    pull()
    // Only poll while the dropdown is open — that's the only time it's read.
    if (!showDropdown) return () => { alive = false }
    const id = setInterval(pull, 15000)
    return () => { alive = false; clearInterval(id) }
  }, [publicKey, isLocked, showDropdown])

  const fmtSol = (b) =>
    b == null ? '—'
      : b >= 1000 ? b.toFixed(0)
      : b >= 1 ? b.toFixed(3)
      : b.toFixed(4)

  const handleImport = async () => {
    if (!keyInput.trim()) return setError('Paste your private key')
    if (!password) return setError('Enter a password')
    if (password !== confirmPw) return setError('Passwords don\'t match')
    if (password.length < 4) return setError('Password too short (min 4)')

    setLoading(true)
    setError('')
    try {
      await importKey(keyInput.trim(), password)
      setKeyInput('')
      setPassword('')
      setConfirmPw('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  // (addwallet-20261027) "+" next to the lock: import another key as the
  // active wallet; the current one is parked encrypted and listed below.
  const handleAdd = async () => {
    if (!keyInput.trim()) return setError('Paste your private key')
    if (!password) return setError('Enter a password')
    if (password !== confirmPw) return setError('Passwords don\'t match')
    if (password.length < 4) return setError('Password too short (min 4)')
    setLoading(true)
    setError('')
    try {
      await addWallet(keyInput.trim(), password)
      setKeyInput(''); setPassword(''); setConfirmPw('')
      setMode(null)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const handleSwitch = (pub) => {
    switchWallet(pub)
    setPassword(''); setError('')
    setMode('unlock')            // the switched-to wallet comes up locked
    setShowDropdown(true)
  }

  const short = (a) => `${a.slice(0, 4)}...${a.slice(-4)}`
  const goPaper = () => { enterPaper(); setMode(null); setError(''); setPassword(''); setShowDropdown(true) }
  const switchList = (otherWallets.length > 0 || paperAdded) && (
    <div className="lw-switch-list">
      {paperAdded && (
        <button className="lw-dropdown-addr lw-switch-row" title="Switch to the Paper wallet (simulated trades)"
          onClick={goPaper}>
          <span style={{ opacity: 0.6 }}>Switch to</span> <span style={{ color: '#facc15', fontWeight: 700 }}>PAPER</span>
        </button>
      )}
      {otherWallets.map(pub => (
        <button key={pub} className="lw-dropdown-addr lw-switch-row" title={`Switch to ${pub}`}
          onClick={() => handleSwitch(pub)}>
          <span style={{ opacity: 0.6 }}>Switch to</span> {short(pub)}
        </button>
      ))}
    </div>
  )

  const handleUnlock = async () => {
    if (!password) return setError('Enter your password')
    setLoading(true)
    setError('')
    try {
      await unlock(password)
      setPassword('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const handleRemove = () => {
    if (confirm(otherWallets.length
      ? 'Remove this wallet? You\'ll need to re-import its key. Your next saved wallet becomes active.'
      : 'Remove wallet? You\'ll need to re-import your key.')) {
      removeWallet()
      setMode(null)
      setShowDropdown(false)
    }
  }

  const handleBioUnlock = async () => {
    setLoading(true)
    setError('')
    try {
      await unlockWithBiometric()
      setPassword('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      // Cancelled, or the authenticator can't derive a key. Either way the
      // password is the way in, so open the menu ON it rather than leaving a
      // dead-end error under a button that just failed.
      setError(err.message)
      setMode('unlock')
      setShowDropdown(true)
    }
    setLoading(false)
  }

  const handleEnableBio = async () => {
    setError('')
    try {
      await enableBiometric()
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (mode === 'import') handleImport()
      else if (mode === 'add') handleAdd()
      else if (mode === 'unlock') handleUnlock()
    }
  }

  const addForm = mode === 'add' && (
              <div className="lw-add-form">
                <textarea
                  ref={inputRef}
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="Private key (base58 or JSON byte array)"
                  className="lw-textarea"
                  rows={2}
                />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown} placeholder="Create password" className="lw-input" />
                <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  onKeyDown={handleKeyDown} placeholder="Confirm password" className="lw-input" />
                <button className="lw-dropdown-action primary" onClick={handleAdd} disabled={loading}>
                  {loading ? 'Encrypting...' : 'Add Wallet'}
                </button>
              </div>
            )
  // "+" menu: add another key wallet, or the Paper wallet (no key, no password).
  const plusMenu = mode === 'plus' && (
    <div className="lw-switch-list">
      <button className="lw-dropdown-addr lw-switch-row" onClick={() => { setMode('add'); setError('') }}>
        Add wallet (key)
      </button>
      {!paperActive && (
        <button className="lw-dropdown-addr lw-switch-row" title="Simulated trades: longs, no fees (buys fill at the offer, sells at the bid), nothing sent on-chain"
          onClick={goPaper}>
          <span style={{ color: '#facc15', fontWeight: 700 }}>Paper</span>
        </button>
      )}
    </div>
  )

  // ─── Paper wallet active (paperwallet-20261027): no key, simulated trades ───
  if (paperActive) {
    const net = paperAcct.realized
    return (
      <div className="local-wallet-wrapper" ref={dropdownRef}>
        <button className="local-wallet-btn connected" style={{ color: '#facc15' }}
          title="Paper wallet — BUY/SELL are simulated, nothing is sent"
          onClick={() => setShowDropdown(p => !p)}>
          PAPER
        </button>
        {showDropdown && (
          <div className="local-wallet-dropdown">
            <div className="lw-addr-row">
              <div className="lw-dropdown-addr" style={{ color: '#facc15', fontWeight: 700 }}
                title="Simulated longs, no fees: buys fill at the offer, sells at the bid. Never on-chain.">
                PAPER
              </div>
              <button
                className="lw-lock-mini"
                onClick={() => { setMode(m => (m === 'add' || m === 'plus' ? null : 'plus')); setError('') }}
                title="Add wallet"
                aria-label="Add wallet"
              >
                <PlusIcon />
              </button>
            </div>
            {plusMenu}
            {addForm}
            <div className="lw-switch-list">
              {hasStoredWallet && activePub && (
                <button className="lw-dropdown-addr lw-switch-row" title={`Switch to ${activePub}`}
                  onClick={() => { exitPaper(); setPassword(''); setError(''); setMode('unlock'); setShowDropdown(true) }}>
                  <span style={{ opacity: 0.6 }}>Switch to</span> {short(activePub)}
                </button>
              )}
              {otherWallets.map(pub => (
                <button key={pub} className="lw-dropdown-addr lw-switch-row" title={`Switch to ${pub}`}
                  onClick={() => { exitPaper(); handleSwitch(pub) }}>
                  <span style={{ opacity: 0.6 }}>Switch to</span> {short(pub)}
                </button>
              ))}
              {!hasStoredWallet && otherWallets.length === 0 && (
                <button className="lw-dropdown-addr lw-switch-row" onClick={() => { exitPaper(); setShowDropdown(false); setMode(null) }}>
                  <span style={{ opacity: 0.6 }}>Leave Paper</span>
                </button>
              )}
            </div>
            <div className="lw-dropdown-addr" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.9 }}>
              <span style={{ opacity: 0.6 }}>Balance</span>
              <span style={{ color: '#c8cfdc', fontWeight: 600 }}>{fmtSol(paperAcct.cash)} SOL</span>
            </div>
            <div className="lw-dropdown-addr" title="Realized PnL (no fees)"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.9 }}>
              <span style={{ opacity: 0.6 }}>Net PnL</span>
              <span style={{ color: net > 0 ? '#22c55e' : net < 0 ? '#ef4444' : '#c8cfdc', fontWeight: 600 }}>
                {net >= 0 ? '+' : ''}{net.toFixed(4)} SOL
              </span>
            </div>
            {error && <div className="lw-error">{error}</div>}
          </div>
        )}
      </div>
    )
  }

  // ─── Unlocked state: show address + lock/remove ───
  if (hasStoredWallet && !isLocked && publicKey) {
    const addr = publicKey.toBase58()
    return (
      <div className="local-wallet-wrapper" ref={dropdownRef}>
        <button
          className="local-wallet-btn connected"
          onClick={() => setShowDropdown(p => !p)}
        >
           {addr.slice(0, 4)}...{addr.slice(-4)}
        </button>

        {showDropdown && (
          <div className="local-wallet-dropdown">
            {/* Relock is a small inline padlock at the head of the address row,
                not the full-height tile the fingerprint uses. Asymmetric on
                purpose: unlocking is the frequent, glanced-at action and earns
                the big target; relocking is occasional and deliberate, and a
                tile that size invites the mis-tap that drops your wallet
                mid-trade. Left of the address because it's a state marker as
                much as a button — the lock you're closing sits with the thing
                it locks. */}
            <div className="lw-addr-row">
              <button
                className="lw-lock-mini"
                onClick={() => { lock(); setShowDropdown(false) }}
                title="Lock wallet"
                aria-label="Lock wallet"
              >
                <PadlockIcon />
              </button>
              <div className="lw-dropdown-addr" title={addr}>
                {addr.slice(0, 8)}...{addr.slice(-8)}
              </div>
              {/* plusstyle-20261027: "+" at the row's right end (address is flex:1). */}
              <button
                className="lw-lock-mini"
                onClick={() => { setMode(m => (m === 'add' || m === 'plus' ? null : 'plus')); setError('') }}
                title="Add wallet"
                aria-label="Add wallet"
              >
                <PlusIcon />
              </button>
            </div>
            {plusMenu}
            {addForm}
            {switchList}
            <div
              className="lw-dropdown-addr"
              title={solBalance == null ? 'Balance unavailable' : `${solBalance} SOL`}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.9 }}
            >
              <span style={{ opacity: 0.6 }}>Balance</span>
              <span style={{ color: '#c8cfdc', fontWeight: 600 }}>{fmtSol(solBalance)} SOL</span>
            </div>
            {(bioAvailable || hasBiometric) && (
              hasBiometric ? (
                <button className="lw-dropdown-action" onClick={disableBiometric}>
                  Turn off fingerprint unlock
                </button>
              ) : (
                <button className="lw-dropdown-action" onClick={handleEnableBio}>
                  Enable fingerprint unlock
                </button>
              )
            )}
            {error && <div className="lw-error">{error}</div>}
            <button className="lw-dropdown-action danger" onClick={handleRemove}>
              Remove Wallet
            </button>
          </div>
        )}
      </div>
    )
  }

  // ─── Locked state: show unlock button ───
  if (hasStoredWallet && isLocked) {
    return (
      <div className="local-wallet-wrapper" ref={dropdownRef}>
        {/* Enrolled → the print replaces the word, and tapping it goes
            straight to the biometric prompt. The LABEL STAYS IN THE DOM at
            opacity 0: it's what sizes the button, so the header can't shift
            by a few px depending on whether a passkey exists. The icon is
            absolutely centred over it. */}
        <button
          className={`local-wallet-btn locked${hasBiometric ? ' has-bio' : ''}`}
          onClick={hasBiometric
            ? handleBioUnlock
            : () => { setMode('unlock'); setShowDropdown(true); setError('') }}
          title={hasBiometric ? 'Unlock with fingerprint' : undefined}
          aria-label={hasBiometric ? 'Unlock with fingerprint' : undefined}
        >
          <span className="lw-btn-label">Unlock</span>
          {hasBiometric && (
            <span className="lw-btn-icon">
              <FingerprintIcon style={{ width: 'auto', height: '100%' }} />
            </span>
          )}
        </button>

        {showDropdown && mode === 'unlock' && (
          <div className="local-wallet-dropdown">
            {/* TITLE LIVES INSIDE THE COLUMN, not above the split. It looks
                identical either way — left-aligned text at the top-left — but
                it changes what the fingerprint centres on. Outside the split,
                the row starts below the title and the print centred on the
                row alone, landing ~11.6px (half the 23.2px title block) below
                the panel's middle: roughly 35px of air above the glyph
                against 12px below. Inside, the split IS the panel interior
                and the existing centring is right with no extra CSS. Don't
                lift it back out. */}
            <div className="lw-dd-split">
            <div className="lw-dd-main">
            <div className="lw-dropdown-title">
              Unlock Local Wallet{otherWallets.length > 0 && activePub ? ` ${short(activePub)}` : ''}
            </div>
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Password"
              className="lw-input"
            />
            <label className="lw-remember" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, opacity: 0.8, margin: '4px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={rememberOn}
                onChange={(e) => { setRememberOn(e.target.checked); setRememberPref(e.target.checked) }}
              />
              Remember for 12h
            </label>
            {error && <div className="lw-error">{error}</div>}
            <button
              className="lw-dropdown-action primary"
              onClick={handleUnlock}
              disabled={loading}
            >
              {loading ? 'Unlocking...' : 'Unlock'}
            </button>
            <button className="lw-dropdown-action danger" onClick={handleRemove}>
              Remove & Re-import
            </button>
            {switchList}
            </div>
            {/* FIRST RUN IS ALWAYS THE PASSWORD: the biometric envelope is
                encrypted with the wallet secret, which only exists in memory
                once you've unlocked — so there is nothing for a fingerprint to
                open until then. Disabled rather than hidden, so the affordance
                is discoverable before it's usable.

                The print is the panel's status light. No passkey → ghost grey
                with "password" stamped across it; passkey → the same
                --connect-accent every other live action wears. The stamp is
                absolutely positioned (CSS .lw-icon-stamp) so it adds zero
                layout: the print sits in the same spot in both states and
                nothing twitches when you enroll. No inline presentation here
                — colour and geometry live in .lw-icon-side / .is-live so the
                palette has one owner.

                ONE WORD, deliberately. "password first" measured wider than
                the 86px button and spilled into the field column, and a stamp
                that overhangs the thing it's stamped on reads as a layout
                bug. One word sits inside the ridges; the tooltip below still
                carries the full sentence for anyone who wants the reason. */}
            <button
              className={`lw-icon-side${hasBiometric ? ' is-live' : ''}`}
              onClick={handleBioUnlock}
              disabled={!hasBiometric || loading}
              title={hasBiometric
                ? 'Unlock with fingerprint'
                : 'Unlock with your password once, then enable fingerprint unlock'}
              aria-label="Unlock with fingerprint"
            >
              <FingerprintIcon />
              {!hasBiometric && (
                <span className="lw-icon-stamp">password</span>
              )}
            </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── No wallet: show import button ───
  return (
    <div className="local-wallet-wrapper" ref={dropdownRef}>
      <button
        className="local-wallet-btn no-wallet"
        onClick={() => { setMode('import'); setShowDropdown(true); setError('') }}
      >
        + Local
      </button>

      {showDropdown && mode === 'import' && (
        <div className="local-wallet-dropdown import-mode">
          <div className="lw-dropdown-title">Import Local Wallet</div>
          <div className="lw-dropdown-hint">
            Stored encrypted in your browser. No confirmations — trades fire instantly.
          </div>
          <textarea
            ref={inputRef}
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Private key (base58 or JSON byte array)"
            className="lw-textarea"
            rows={2}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Create password"
            className="lw-input"
          />
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Confirm password"
            className="lw-input"
          />
          {error && <div className="lw-error">{error}</div>}
          <button
            className="lw-dropdown-action primary"
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? 'Encrypting...' : 'Import & Encrypt'}
          </button>
        </div>
      )}
    </div>
  )
}